import * as uuidgen from 'uuid';
import { getRawConfigValue, runtimeEnvironment } from '@/utils/runtimeEnvironment';

export const LAST_VISITED_NOTIFICATIONS_STORAGE_KEY = 'last-visited-notifications';
export const LAST_SEEN_STATUS_ALERT_STORAGE_KEY = 'last-seen-status-alert';

export class MetaState {
    constructor(
        readonly errors: VegkartError[] = [],
        readonly lastHash: string = '',
        readonly notifications: Notification[] = validateNotifications(
            defaultNotifications
        ),
        readonly lastVisitedNotifications: Date = getLastVisitedNotifications()
    ) {}

    override(modifyObject: { [P in keyof MetaState]?: MetaState[P] }): MetaState {
        return Object.assign(Object.create(MetaState.prototype), {
            ...this,
            ...modifyObject,
        });
    }

    withErrors(errors: VegkartError[]): MetaState {
        return this.override({ errors });
    }

    withLastHash(lastHash: string): MetaState {
        return this.override({ lastHash });
    }

    withNotifications(notifications: Notification[]): MetaState {
        return this.override({ notifications });
    }

    withLastVisitedNotifications(lastVisitedNotifications: Date): MetaState {
        return this.override({ lastVisitedNotifications });
    }
}

export class VegkartError extends Error {
    private __proto__: VegkartError;
    readonly uuid = uuidgen.v4();
    public readonly url: string;

    constructor(
        readonly message: string,
        readonly params?: { [key: string]: unknown },
        readonly severity: Severity = Severity.ERROR
    ) {
        super(message);
        this.url = window.window.location.toString();
        this.__proto__ = VegkartError.prototype;
    }
}

export type Notification = {
    time: Date;
    message: HTMLElement | string;
    callback?: () => void;
    environments?: string[];
};

export enum Severity {
    ERROR = 'ERROR',
    WARN = 'WARN',
    INFO = 'INFO',
}

const getLastVisitedNotifications = () => {
    const value = localStorage.getItem(LAST_VISITED_NOTIFICATIONS_STORAGE_KEY);
    return new Date(value ?? 0);
};
const validateNotifications = (notifications: Notification[]) => {
    const env = runtimeEnvironment();
    const now = new Date();
    return notifications.filter(
        n =>
            n.time instanceof Date &&
            !isNaN(n.time.getTime()) &&
            n.time <= now &&
            (!n.environments || n.environments.includes(env))
    );
};

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

function getNibNotification(): Notification | null {
    const expiryStr = getRawConfigValue('nib_token_expiry');
    if (!expiryStr) return null;
    const expiryDate = new Date(expiryStr);
    if (isNaN(expiryDate.getTime())) return null;
    const notifyTime = new Date(expiryDate.getTime() - THIRTY_DAYS_MS);
    const formattedExpiry = expiryDate.toLocaleDateString('nb-NO');
    return {
        time: notifyTime,
        message: `
            <p><strong>NiB-token utløper snart!</strong>
            Token for Norge i bilder utløper ${formattedExpiry}.
            Generer nye tokens med <code>scripts/generate-nib-tokens.sh</code>.
            Se docs/nib-token.md for veiledning.</p>
        `,
        environments: ['dev', 'local'],
    };
}

// Simple way of adding notifications.
// If these notifications are frequently modified or need to be shipped to production fast,
// please consider creating an actual external service that supplies messages to Vegkart.
// Notifications are not considered permanent and may be removed from this list if no longer relevant.
const defaultNotifications: Notification[] = [
    // {
    //     time: new Date('2025-06-11T08:00:00.000+01:00'),
    //     message: `
    //         <p className="c-preferences-warning-text">
    //             API-v4 er standardvalget i Vegkart. API-v3 avvikles snart, men kan brukes hvis API-v4
    //             skaper problemer. Vi evaluerer API-v4 og kan rulle tilbake om nødvendig.
    //         </p>
    //         <p className="c-preferences-warning-text">
    //             Følg med på nvdb.no for oppdatert informasjon:
    //         </p>
    //         <a
    //             href="https://www.nvdb.no/aktuelt/"
    //             target="_blank"
    //             rel="noopener noreferrer"
    //         >
    //             https://www.nvdb.no/aktuelt/
    //         </a>
    //     `,
    //     callback: () => localStorage.setItem('VISITED_API_INFORMATION', true),
    // },
    ...(getNibNotification() ? [getNibNotification() as Notification] : []),
];
